import type { PerformanceSummary } from '../types.js'
import { TIER_LABELS } from '../../../modules/core/index.js'
import { WORKER_ROLE_LABELS } from '../../../modules/core/index.js'
import { MONTH_STATE_LABELS, TIER_REASON_LABELS, formatWhen, monthLabel, percent, personLabel, spanLabel } from '../labels.js'
import { SummaryScoreChart } from './SummaryScoreChart.js'
import { PerformanceHighlights } from './PerformanceHighlights.js'

const number = (value: number | null): string => (value === null ? '—' : String(value))
const tier = (value: PerformanceSummary['tierAtStart']): string => (value === null ? '—' : TIER_LABELS[value])

function Facts({ facts }: { facts: readonly [string, string][] }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] sm:grid-cols-3">
      {facts.map(([label, value]) => (
        <div key={label}>
          <dt className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">{label}</dt>
          <dd className="text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function Heading({ children }: { children: string }) {
  return <h3 className="mt-5 mb-1.5 border-b border-ink/15 pb-1 text-[10px] font-bold uppercase tracking-wide text-ink">{children}</h3>
}

const CELL = 'px-2 py-1 text-left align-top'
const HEAD = `${CELL} text-[9px] font-bold uppercase tracking-wide text-ink-mid border-b border-ink/20`

/**
 * A performance summary as a document: what is printed, with the
 * browser's Save as PDF, for the employee file. Black on white, every
 * section the record has, the fingerprint at the end so the printed page
 * can be matched back to the record. Nothing here edits.
 */
export function SummaryDocument({ summary }: { summary: PerformanceSummary }) {
  const facilityName = summary.facilities.find((facility) => facility.facilityId === summary.facilityId)?.name ?? summary.facilityId
  const { shifts, barriers } = summary
  const messages = Object.entries(summary.messages).filter(([, count]) => count > 0)

  return (
    <article className="bg-white px-6 py-5 text-ink">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b-2 border-ink pb-2">
        <div>
          <div className="text-[9px] font-black uppercase tracking-[2.5px]">SeeWhoCares</div>
          <h2 className="text-lg font-black">Performance summary</h2>
        </div>
        <div className="text-[11px] text-ink-mid">{spanLabel(summary.span)}</div>
      </header>

      <div className="mt-3">
        <Facts
          facts={[
            ['Staff member', summary.worker.name],
            ['Role', `${summary.worker.role} · ${WORKER_ROLE_LABELS[summary.worker.role]}`],
            ['Employee ID', summary.employeeId ?? 'Not on record'],
            ['Facility', facilityName],
            ['Hire date', summary.worker.hireDate ?? 'Not recorded'],
            ['Tenure at span end', summary.worker.tenureMonthsAtSpanEnd === null ? '—' : `${summary.worker.tenureMonthsAtSpanEnd} months`],
            ['Generated', formatWhen(summary.generatedAt)],
            ['Generated by', personLabel(summary.generatedBy)],
            ['Worker ID', summary.workerId],
          ]}
        />
        {summary.note && (
          <p className="mt-2 text-[11px]">
            <span className="font-bold">Note: </span>
            {summary.note}
          </p>
        )}
        <p className="mt-2 text-[11px] italic text-ink-mid">{summary.provenance.statement}</p>
      </div>

      <Heading>The span at a glance</Heading>
      <PerformanceHighlights summary={summary} compact />
      <Facts
        facts={[
          ['Worked', `${shifts.completed} of ${shifts.scheduled} scheduled · to the end ${percent(shifts.workedToEnd, shifts.completed)}`],
          ['Clinical Bridge shifts', String(shifts.clinicalBridge)],
          ['Notes raised by the worker', `${barriers.raised}${barriers.raised > 0 ? ` · ${barriers.replied} answered` : ''}`],
        ]}
      />

      <Heading>Month by month</Heading>
      <SummaryScoreChart months={summary.months} />
      <table className="w-full text-[11px]">
        <thead>
          <tr>
            <th className={HEAD}>Month</th>
            <th className={HEAD}>State</th>
            <th className={HEAD}>Score</th>
            <th className={HEAD}>Tier</th>
            <th className={HEAD}>How the close set it</th>
            <th className={HEAD}>Versions</th>
          </tr>
        </thead>
        <tbody>
          {summary.months.map((month) => (
            <tr key={month.period} className="border-b border-ink/10">
              <td className={CELL}>{monthLabel(month.period)}</td>
              <td className={CELL}>{MONTH_STATE_LABELS[month.state]}</td>
              <td className={CELL}>{month.score === null ? '—' : `${month.score}${month.provisional ? ' (provisional)' : ''}`}</td>
              <td className={CELL}>{tier(month.tier)}</td>
              <td className={CELL}>
                {month.tierReason ? (TIER_REASON_LABELS[month.tierReason] ?? month.tierReason) : '—'}
                {month.tierSetByHand && <span className="ml-1 font-bold">· started at a tier set by hand</span>}
              </td>
              <td className={`${CELL} text-ink-mid`}>{month.engineVersion ? `engine ${month.engineVersion}${month.behaviorsVersion === null ? '' : ` · behaviors ${month.behaviorsVersion}`}` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <Heading>The span by behavior</Heading>
      <table className="w-full text-[11px]">
        <thead>
          <tr>
            <th className={HEAD}>Behavior</th>
            <th className={HEAD}>Over the span</th>
            <th className={HEAD}>Avg points lost</th>
            <th className={HEAD}>Worst month</th>
            {summary.months.map((month) => (
              <th key={month.period} className={`${HEAD} text-right`}>
                {month.period.slice(5, 7)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {summary.behaviors.map((behavior) => (
            <tr key={behavior.key} className="border-b border-ink/10">
              <td className={CELL}>
                {behavior.label}
                {!behavior.standard && <span className="ml-1 text-[9px] uppercase text-ink-mid">custom</span>}
              </td>
              <td className={CELL}>{behavior.numerator === null ? '—' : behavior.denominator === null ? `${behavior.numerator} occurrences` : `${behavior.numerator} of ${behavior.denominator}`}</td>
              <td className={CELL}>{number(behavior.averagePointsLost)}</td>
              <td className={CELL}>{behavior.worstPeriod ? monthLabel(behavior.worstPeriod) : '—'}</td>
              {behavior.months.map((month) => (
                <td key={month.period} className={`${CELL} text-right tabular-nums`}>
                  {month.included && month.value !== null ? Math.round(month.value) : '·'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-1 text-[10px] text-ink-mid">Month columns show each behavior's 0 to 100 value for that month; a dot is a month that was not scored.</p>

      {(summary.customEvents.length > 0 || barriers.behaviors.length > 0 || messages.length > 0) && (
        <>
          <Heading>Also counted</Heading>
          <Facts
            facts={[
              ...summary.customEvents.map((event) => [event.label, `${event.count} reported`] as [string, string]),
              ...barriers.behaviors.map((behavior) => [`Notes about ${behavior.label}`, String(behavior.count)] as [string, string]),
              ...messages.map(([type, count]) => [`Messages: ${type.replace(/_/g, ' ')}`, String(count)] as [string, string]),
            ]}
          />
        </>
      )}

      <Heading>How this was produced</Heading>
      <p className="text-[11px] text-ink-mid">{summary.provenance.method}</p>
      <p className="mt-1 text-[10px] text-ink-mid">
        Engine {summary.provenance.engineVersions.join(', ') || '—'} · behaviors versions {summary.provenance.behaviorsVersions.join(', ') || '—'} · record {summary.summaryId}
      </p>
      <p className="mt-1 break-all font-mono text-[10px] text-ink-mid">Fingerprint {summary.contentHash}</p>
    </article>
  )
}
